export type EngineId = 'cesium' | 'maplibre' | 'native';

export type InteractionMode =
  | 'select'
  | 'inspect'
  | 'draw-point'
  | 'draw-line'
  | 'draw-polygon'
  | 'measure-distance'
  | 'measure-area';

export type Position = [longitude: number, latitude: number, height?: number];

export type CameraState = {
  longitude: number;
  latitude: number;
  height: number;
  heading?: number;
  pitch?: number;
  roll?: number;
};

export type TimeContext = {
  mode: 'live' | 'historical' | 'forecast' | 'scenario';
  currentTime: string;
  range?: { from: string | null; to: string | null };
  playbackSpeed?: number;
};

export type Geometry =
  | { type: 'Point'; coordinates: Position }
  | { type: 'MultiPoint'; coordinates: Position[] }
  | { type: 'LineString'; coordinates: Position[] }
  | { type: 'MultiLineString'; coordinates: Position[][] }
  | { type: 'Polygon'; coordinates: Position[][] }
  | { type: 'MultiPolygon'; coordinates: Position[][][] };

export type SpatialFeature = {
  type: 'Feature';
  id?: string | number;
  geometry: Geometry | null;
  properties?: Record<string, unknown>;
};

export type FeatureCollection = {
  type: 'FeatureCollection';
  features: SpatialFeature[];
};

export type LayerStyle = {
  color?: string;
  fillColor?: string;
  outlineColor?: string;
  width?: number;
  radius?: number;
  opacity?: number;
  labelField?: string;
  icon?: string;
  clampToGround?: boolean;
  dash?: number[];
  zIndex?: number;
};

export type LayerManifest = {
  id: string;
  title: string;
  kind: 'geojson' | 'entities' | 'raster' | 'three-d-tiles';
  visible?: boolean;
  opacity?: number;
  interactive?: boolean;
  data?: FeatureCollection | SpatialFeature[];
  url?: string;
  attribution?: string;
  style?: LayerStyle;
  metadata?: {
    source?: string;
    observedAt?: string;
    stateType?: 'observed' | 'inferred' | 'forecast' | 'simulated' | 'synthetic';
    classification?: 'public' | 'internal' | 'restricted' | 'sensitive';
  };
};

export type Selection = {
  type: 'none' | 'feature' | 'area';
  layerId?: string;
  featureId?: string | number;
  feature?: SpatialFeature;
  screen?: { x: number; y: number };
  position?: Position;
};

export type DrawResult = {
  mode: InteractionMode;
  geometry: Geometry;
  distanceMeters?: number;
  areaSquareMeters?: number;
};

export type PerformanceMetrics = {
  engine: EngineId;
  fps: number;
  visibleFeatures: number;
  layerCount: number;
  renderDurationMs?: number;
  memoryEstimateMb?: number;
};

export type RendererEventMap = {
  ready: { engine: EngineId };
  cameraChanged: CameraState;
  selectionChanged: Selection;
  layerLoaded: { layerId: string; featureCount?: number };
  layerFailed: { layerId: string; error: Error };
  drawCompleted: DrawResult;
  measureUpdated: DrawResult;
  timeChanged: TimeContext;
  performanceDegraded: PerformanceMetrics;
  error: { error: Error; recoverable: boolean };
};

export type RendererEventName = keyof RendererEventMap;
export type RendererEventHandler<K extends RendererEventName> = (payload: RendererEventMap[K]) => void;

export type RendererMountOptions = {
  initialCamera: CameraState;
  resetCamera?: CameraState;
  time?: TimeContext;
  backgroundColor?: string;
  baseTileUrl?: string;
  baseTileAttribution?: string;
  cesiumIonToken?: string;
  enableTerrain?: boolean;
  enableBuildings?: boolean;
  reducedMotion?: boolean;
};

export interface WorldRenderer {
  readonly id: EngineId;
  mount(container: HTMLElement, options: RendererMountOptions): Promise<void>;
  destroy(): void;
  on<K extends RendererEventName>(event: K, handler: RendererEventHandler<K>): () => void;
  setCamera(cameraState: CameraState): void;
  getCamera(): CameraState;
  flyTo(target: CameraState | Position | Geometry, options?: { durationMs?: number }): Promise<void>;
  zoomBy(factor: number): void;
  resetCamera(): void;
  setAutoRotate(enabled: boolean): void;
  addLayer(layerManifest: LayerManifest): Promise<void>;
  updateLayer(layerManifest: LayerManifest): Promise<void>;
  removeLayer(layerId: string): void;
  setLayerVisibility(layerId: string, visible: boolean): void;
  setLayerOpacity(layerId: string, opacity: number): void;
  setLayerStyle(layerId: string, style: LayerStyle): void;
  reorderLayer(layerId: string, beforeLayerId?: string): void;
  setTime(timeContext: TimeContext): void;
  setInteractionMode(mode: InteractionMode): void;
  clearTransient(): void;
  pick(screenPosition: { x: number; y: number }): Promise<Selection>;
  highlight(selection: Selection): void;
  captureSnapshot(options?: { mimeType?: string; quality?: number }): Promise<Blob>;
  exportVisibleFeatures(): FeatureCollection;
  getPerformanceMetrics(): PerformanceMetrics;
}

export const DEFAULT_CAMERA: CameraState = {
  longitude: 10,
  latitude: 18,
  height: 18_500_000,
  heading: 0,
  pitch: -90,
  roll: 0,
};
